const sameId = (a, b) => a != null && b != null && String(a) === String(b);

export function realtimeKind(event, payload) {
  if (!payload || payload.id == null) return null;
  if (event === "update_Bomba") return "pump";
  if (event === "update_ControlerBomba") return "controller";
  if (["update_elevatoria", "update_poco"].includes(event) &&
      (Object.prototype.hasOwnProperty.call(payload, "altura_poco") ||
       Object.prototype.hasOwnProperty.call(payload, "nivel_atual"))) return "pit";
  return null;
}

function mergeEntity(current, patch) {
  const next = { ...current };
  for (const [key, value] of Object.entries(patch)) {
    if (value === undefined || ["__proto__", "constructor", "prototype"].includes(key)) continue;
    if (["bombas", "bombaControler"].includes(key) && Array.isArray(value)) {
      const existing = current[key] || [];
      next[key] = existing.map(item => {
        const update = value.find(row => sameId(row.id, item.id));
        return update ? mergeEntity(item, update) : item;
      });
      for (const item of value) {
        if (!existing.some(row => sameId(row.id, item.id))) next[key].push(item);
      }
    } else if (value && !Array.isArray(value) && typeof value === "object") {
      next[key] = mergeEntity(current[key] || {}, value);
    } else {
      next[key] = value;
    }
  }
  return next;
}

export function applyStationEvent(stations, event, payload) {
  const kind = realtimeKind(event, payload);
  if (!kind) return stations;
  let changed = false;
  const result = stations.map(station => {
    let stationChanged = false;
    const pocos = (station.pocos || []).map(pit => {
      if (kind === "pit" && sameId(pit.id, payload.id)) {
        stationChanged = true;
        return mergeEntity(pit, payload);
      }
      if (kind === "pit") return pit;
      let pitChanged = false;
      const bombas = (pit.bombas || []).map(pump => {
        if (kind === "pump" && sameId(pump.id, payload.id)) {
          pitChanged = true;
          // Controller acknowledgements have their own event; pump snapshots may
          // still contain the controller loaded before that acknowledgement.
          return mergeEntity(pump, { ...payload, bombaControler: undefined });
        }
        if (kind !== "controller") return pump;
        const controls = pump.bombaControler || [];
        const existing = controls.some(control => sameId(control.id, payload.id));
        if (!existing && !sameId(pump.id, payload.bomba?.id)) return pump;
        pitChanged = true;
        return mergeEntity(pump, { bombaControler: [payload] });
      });
      if (!pitChanged) return pit;
      stationChanged = true;
      return { ...pit, bombas };
    });
    if (!stationChanged) return station;
    changed = true;
    return { ...station, pocos };
  });
  return changed ? result : stations;
}

// Replay events received while an HTTP snapshot was in flight.
export function createStationEventBuffer() {
  let revision = 0;
  const entries = [];
  return {
    get revision() { return revision; },
    record(event, payload) {
      const kind = realtimeKind(event, payload);
      if (!kind) return false;
      entries.push({ event, payload, revision: ++revision });
      if (entries.length > 1000) entries.shift();
      return true;
    },
    replay(stations, since) {
      return entries.filter(entry => entry.revision > since)
        .reduce((result, entry) => applyStationEvent(result, entry.event, entry.payload), stations);
    },
  };
}

export function createRefreshScheduler(refresh, delay = 150) {
  let timer = null;
  let running = false;
  let queued = false;
  let disposed = false;
  const schedule = () => {
    if (disposed) return;
    if (running) { queued = true; return; }
    if (timer !== null) return;
    timer = setTimeout(async () => {
      timer = null;
      running = true;
      try { await refresh(); }
      catch (error) { console.error("Falha ao sincronizar dados em tempo real", error); }
      finally {
        running = false;
        if (queued) { queued = false; schedule(); }
      }
    }, delay);
  };
  schedule.dispose = () => { disposed = true; clearTimeout(timer); };
  return schedule;
}
